import { cloneElement, memo, forwardRef } from "react";
import type { DirectionSegment as Segment } from "../../types/directionsTypes";
import { IMAGES } from "../svgImagesByType";
import { useMapRouteStore } from "@/stores/mapRouteStore";

type SegmentationStepProps = {
  currentSegment: Segment;
  segments: Segment[];
  index: number;
  isActive?: boolean;
  isNavigationMode?: boolean;
};

const SegmentationStep = memo(
  forwardRef<HTMLDivElement, SegmentationStepProps>(
    function SegmentationStep(
      {
        currentSegment,
        segments,
        index,
        isActive = false,
        isNavigationMode = false,
      },
      ref,
    ) {
      const setSelectedSegmentId = useMapRouteStore(
        (s) => s.setSelectedSegmentId,
      );

      const handleIconClick = () => {
        if (isNavigationMode) return;
        setSelectedSegmentId(currentSegment.id);
      };

      const isFirst = index === 0;
      const isLast = index === segments.length - 1;

      const formatTime = (seconds: number) => {
        if (seconds === 0) return null;
        const minutes = Math.ceil(seconds / 60);
        return `${minutes} мин`;
      };

      const time = formatTime(currentSegment.time);

      // Determine icon style based on position and active state
      const getIconStyle = (): React.CSSProperties => {
        if (isFirst) {
          return { backgroundColor: "#1a5fb4", borderColor: "#1a5fb4" };
        }
        if (isLast) {
          return { backgroundColor: "#C4A962", borderColor: "#C4A962" };
        }
        if (isActive) {
          return {
            backgroundColor: "#1a5fb4",
            borderColor: "#1a5fb4",
            boxShadow: "0 0 0 4px rgba(26,95,180,0.15)",
          };
        }
        return { backgroundColor: "#ffffff", borderColor: "#e5e7eb" };
      };

      return (
        <div
          ref={ref}
          className={`flex items-start gap-[14px] py-[10px] px-4 rounded-lg relative transition-all ${
            isActive
              ? "bg-gradient-to-r from-[rgba(26,95,180,0.08)] to-transparent"
              : ""
          }`}
        >
          {/* Icon */}
          <div
            className={`w-8 h-8 rounded-full border-2 flex items-center justify-center shrink-0 z-10 transition-all ${
              isNavigationMode ? "cursor-default" : "cursor-pointer"
            }`}
            style={getIconStyle()}
            onClick={handleIconClick}
          >
            {cloneElement(IMAGES[currentSegment.imageType], {
              className: `w-4 h-4 ${isFirst || isLast || isActive ? "text-white" : "text-gray-500"}`,
            })}
          </div>

          {/* Content */}
          <div className="flex-1 pt-[5px] min-w-0">
            <div className="text-sm font-medium text-[#1a2138] mb-0.5">
              {currentSegment.title}
            </div>
            {currentSegment.subtitle && (
              <div className="text-xs text-[#8b95a5]">
                {currentSegment.subtitle}
              </div>
            )}
          </div>

          {/* Time */}
          {time && (
            <div className="pt-[5px] text-right shrink-0">
              <span className="text-xs font-semibold text-[#1a5fb4]">
                {time}
              </span>
            </div>
          )}
        </div>
      );
    },
  ),
  (prev, next) =>
    prev.index === next.index &&
    prev.isActive === next.isActive &&
    prev.isNavigationMode === next.isNavigationMode &&
    prev.currentSegment.id === next.currentSegment.id &&
    prev.segments.length === next.segments.length,
);

export default SegmentationStep;
